<template>
<v-card :width="width" class="fill-height d-flex flex-column align-content-space-between justify-space-between " :class="getRequireQuiz(category) ? 'quiz-warning-card' : 'quiz-card'">
  <v-card-title>
    <div class="d-flex justify-space-between w-full align-center">
      <LTimerPill v-if="!getRequireQuiz(category) && lastPositiveAttempt"
                  icon-timer-inactive="mdi-replay"
                  :end-date="DateUtils.getStartOfDate(moment(lastPositiveAttempt.allowReattemptAt), 'day',)"
                  :tooltip-title="quizInfoConfig[category]?.tooltipTitle()"
                  :tooltip-text="quizInfoConfig[category]?.tooltipText()"
                  icon-timer-active="mdi-replay"></LTimerPill>
      <LTimerPill v-else
                  :tooltip-title="quizInfoConfig[category]?.tooltipTitleRequireTest()"
                  :tooltip-text="quizInfoConfig[category]?.tooltipTextRequireTest()"
                  icon-timer-inactive="mdi-lock-outline"
                  color="warning"
                  text-color="anchor"
                  :no-opacity="true"
                  @timerEnded="handleHRlockTimerEnded"
                  :end-date="getRequireHrLockAt(category)"
                  class="text-warning-onBase-highEmphasis!"
                  icon-timer-active="mdi-lock-open-outline" />
      <v-tooltip max-width="365px" top>
        <template v-slot:activator="{on}">
          <div v-on="on">
              <div class="circle-box d-flex align-center justify-center" :class="getRequireQuiz(category) ? 'surface' : 'background'">
                <v-icon color="on-background" small>mdi-alert-circle-outline</v-icon>
              </div>
          </div>
        </template>
        {{quizInfoConfig[category]?.tooltipDescription}}
      </v-tooltip>
    </div>
  </v-card-title>
  <v-card-text class="pb-0">
    <v-spacer></v-spacer>
      <lenda-icon :src="getQuizIcon(props.category)" width="68px" height="68px" />
    <div class="mt-5">
      <div v-if="!getRequireQuiz(props.category)" class="status-badge success mb-3">
        <v-icon size="16px" color="success">mdi-checkbox-marked-circle-outline</v-icon>
        <span class="ml-1">{{ quizInfoConfig[category]?.approvedText }}</span>
      </div>
      <span v-else class="block mb-1 text-core-onCore-lowEmphasis">{{ quizInfoConfig[category]?.quizDuration }}</span>
      <p class="headline-5 mb-1">{{quizInfoConfig[category]?.quizTitle}}</p>
      <p class="body-2 mb-0 text-core-onCore-lowEmphasis">
        {{quizInfoConfig[category]?.quizDescription}}
      </p>
    </div>
  </v-card-text>
  <v-card-actions class="w-full">
    <v-btn
      v-if="quizInfoConfig[category]?.showTestBtn && !getRequireQuiz(props.category)"
      @click="onTakeQuiz"
      outlined
      min-width="100%"
      text
    >
      Zobacz
    </v-btn>
    <v-btn
      v-if="getRequireQuiz(props.category)"
      @click="onTakeQuiz"
      color="primary"
      min-width="100%"
      depressed
      :disabled="hasHrLockTimerEnded"
    >
     {{quizInfoConfig[category]?.startTestBtnText}}
    </v-btn>
  </v-card-actions>
</v-card>
</template>
<script setup lang="ts">
import {QuizTestAttempt} from '@/modules/bhp/models/QuizTest';
import GoToService from '@/router/GoToService';
import LTimerPill from '@/components/commons/LTimerPill.vue';
import DateUtils from '@/services/utils/DateUtils';
import {ref} from 'vue';
import {QuizCategory} from '@/models/Quiz';
import AuthService from '@/modules/user/AuthService';
import moment from 'moment/moment';
import { capitalize } from '@/composables/useFilters';
import LendaIcon from '@/components/commons/LendaIcon.vue';

const props = withDefaults(defineProps<{
  lastPositiveAttempt?: QuizTestAttempt,
  category: QuizCategory,
  width?: string | number,
}>(), {
  width: 256,
});

const onTakeQuiz = () => {
  GoToService[props.category]();
};


const handleHRlockTimerEnded = () => {
  hasHrLockTimerEnded.value = true;
}

const hasHrLockTimerEnded = ref(false)

const getRequireHrLockAt = (category: QuizCategory) => {
  return AuthService.User?.quiz?.[`require${capitalize(category)}HrLockAt`]!
};

const getRequireQuiz = (category: QuizCategory) => {
  return AuthService.User?.quiz?.[`require${capitalize(category)}`]!
};

const getQuizIcon = (category: QuizCategory) => {
  return quizInfoConfig[category]?.pathToIcon ? require(`@/assets/icons/quizes/${quizInfoConfig[props.category]?.pathToIcon}`) : ''
}

type QuizInfoConfig = {
  pathToIcon?: string;
  quizTitle: string;
  quizDescription: string;
  quizDuration: string;
  approvedText: string;
  showTestBtn: boolean;
  tooltipTextRequireTest: () => string;
  tooltipTitleRequireTest: () => string;
  tooltipText: () => string;
  tooltipTitle: () => string;
  tooltipDescription: string;
  startTestBtnText: string;
}
const quizInfoConfig: Partial<Record<QuizCategory,QuizInfoConfig>> = {
  [QuizCategory.STMT]: {
    quizTitle: 'Oświadczenia akredytacyjne',
    quizDuration: 'Zajmie 20 minut',
    pathToIcon: 'statements.svg',
    approvedText: 'Potwierdzone',
    showTestBtn: true,
    startTestBtnText: 'Uzupełnij',
    tooltipTextRequireTest: () => 'Po przekroczeniu terminu dostęp do systemu zostanie zablokowany',
    tooltipTitleRequireTest: () => `Ostateczny termin potwierdzenia: ${DateUtils.getFormatedDate(getRequireHrLockAt(props.category))}`,
    tooltipText: () => `Możliwość potwierdzenia będzie dostępna przez ${DateUtils.diff(props.lastPositiveAttempt?.hrLockAt!, props.lastPositiveAttempt?.allowReattemptAt!, 'days',)} dni. W przypadku braku potwierdzenia po tym czasie system zostanie zablokowany.`,
    tooltipTitle: () => `Kolejna możliwość zdania testu: ${DateUtils.getFormatedDate(props.lastPositiveAttempt?.allowReattemptAt!)}`,
    quizDescription: 'Potwierdzenie możliwości dalszego wykonywania zawodu eksperta finansowego',
    tooltipDescription: 'Z uwagi na obowiązki wynikające z przepisów prawa, a także z zawartych umów z bankami, zobowiązany jesteś do złożenia oświadczeń na Platformie Lendi. W przypadku braku potwierdzenia w wyznaczonym terminie, Twoja umowa z Lendi Sp. z o.o. zostanie rozwiązana. Oświadczenia odnawiane są cyklicznie – nie częściej niż raz w roku.',
  },
  [QuizCategory.ETHIC]: {
    quizTitle: 'Szkolenie Etyka i Bezpieczeństwo w sektorze finansowym.',
    pathToIcon: 'ethic.svg',
    quizDuration: 'Zajmie 45 minut',
    approvedText: 'Zdane',
    showTestBtn: false,
    startTestBtnText: 'Rozpocznij',
    tooltipTextRequireTest: () => 'Po przekroczeniu terminu dostęp do systemu zostanie zablokowany',
    tooltipTitleRequireTest: () => `Ostateczny termin zdania: ${DateUtils.getFormatedDate(getRequireHrLockAt(props.category))}`,
    tooltipText: () => `Możliwość zdania będzie dostępna przez ${DateUtils.diff(props.lastPositiveAttempt?.hrLockAt!, props.lastPositiveAttempt?.allowReattemptAt!, 'days',)} dni. W przypadku braku zdania po tym czasie system zostanie zablokowany.`,
    tooltipTitle: () => `Kolejna możliwość zdania testu: ${DateUtils.getFormatedDate(props.lastPositiveAttempt?.allowReattemptAt!)}`,
    quizDescription: 'Niezbędne do spełniania wymogów Lendi i instytucji finansowych, koncentruje się na kluczowych zasadach etycznych oraz najlepszych praktykach w zakresie ochrony danych i zarządzania ryzykiem.',
    tooltipDescription:  'Z uwagi na obowiązki wynikające z przepisów prawa, a także z zawartych umów z bankami, zobowiązany jesteś do realizacji szkoleń udostępnianych na Platformie Lendi. W przypadku braku zdania w wyznaczonym terminie, Twoja umowa z Lendi Sp. z o.o. zostanie rozwiązana. Szkolenia odnawiane są cyklicznie – nie częściej niż raz w roku.',
  },
  [QuizCategory.BANK_STMTS]: {
    quizTitle: 'Oświadczenia bankowe',
    quizDuration: 'Zajmie 20 minut',
    pathToIcon: 'bank_statements.svg',
    approvedText: 'Potwierdzone',
    showTestBtn: true,
    startTestBtnText: 'Uzupełnij',
    tooltipTextRequireTest: () => 'Po przekroczeniu terminu dostęp do systemu zostanie zablokowany',
    tooltipTitleRequireTest: () => `Ostateczny termin potwierdzenia: ${DateUtils.getFormatedDate(getRequireHrLockAt(props.category))}`,
    tooltipText: () => `Możliwość potwierdzenia będzie dostępna przez ${DateUtils.diff(props.lastPositiveAttempt?.hrLockAt!, props.lastPositiveAttempt?.allowReattemptAt!, 'days',)} dni. W przypadku braku potwierdzenia po tym czasie system zostanie zablokowany.`,
    tooltipTitle: () => `Kolejna możliwość zdania testu: ${DateUtils.getFormatedDate(props.lastPositiveAttempt?.allowReattemptAt!)}`,
    quizDescription: 'Warunek uzyskania i zachowania uprawnień eksperta finansowego.',
    tooltipDescription: 'Z uwagi na obowiązki wynikające z zawartych umów z bankami, zobowiązany jesteś do złożenia oświadczeń bankowych na Platformie Lendi. Brak potwierdzenia w wyznaczonym terminie spowoduje odebranie uprawnień w instytucjach finansowych oraz blokadę Platformy Lendi. Oświadczenia odnawiane cyklicznie - nie częściej niż raz w roku.',
  },
  [QuizCategory.KNF]: {
    quizTitle: 'Test KNF',
    pathToIcon: 'knf.svg',
    quizDuration: 'Zajmie 60 minut',
    approvedText: 'Zdane',
    showTestBtn: false,
    startTestBtnText: 'Rozpocznij',
    tooltipTextRequireTest: () => 'Po przekroczeniu terminu dostęp do systemu zostanie zablokowany',
    tooltipTitleRequireTest: () => `Ostateczny termin zdania: ${DateUtils.getFormatedDate(getRequireHrLockAt(props.category))}`,
    tooltipText: () => `Możliwość zdania będzie dostępna przez ${DateUtils.diff(props.lastPositiveAttempt?.hrLockAt!, props.lastPositiveAttempt?.allowReattemptAt!, 'days',)} dni. W przypadku braku zdania po tym czasie system zostanie zablokowany.`,
    tooltipTitle: () => `Kolejna możliwość zdania testu: ${DateUtils.getFormatedDate(props.lastPositiveAttempt?.allowReattemptAt!)}`,
    quizDescription: 'Egzamin sprawdzający kompetencje z zakresu wymagań Komisji Nadzoru Finansowego.',
    tooltipDescription:  'Z uwagi na obowiązki wynikające z przepisów prawa oraz umów zawartych z bankami, jesteś zobowiązany do przystąpienia do testów na Platformie Lendi. W przypadku nieprzystąpienia w wyznaczonym terminie, Twoja umowa z Lendi Sp. z o.o. zostanie rozwiązana. Testy odnawiane są cyklicznie – nie częściej niż raz w roku. ',
  },
}

</script>

<style scoped lang="scss">
.quiz-warning-card {
  background-color: color-mix(in srgb, var(--token-warning-base) 12%, var(--token-core-surface)) !important;
  border-color: var(--token-warning-base) !important;
}
.quiz-card {
  background-color: var(--token-core-surface) !important;
  border-color: var(--token-core-border) !important;
}
.circle-box {
  width: 32px;
  height: 28px;
  border: 1px solid var(--token-core-border) !important;
  border-radius: 24px;
}
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 14px;
  line-height: 1;
  &.success {
    background-color: #EBFEF1 !important;
    color: #077E2F !important;
    .v-icon {
      color: #077E2F !important;
    }
  }
}
</style>
